import { resolveAutoLanguage } from './languageDetection';

interface AgentTranslation {
  languageId: string;
  name: string;
  description: string;
  chatTitle: string;
  welcomeMessage: string;
}

interface Agent {
  id: string;
  translations?: AgentTranslation[];
  [key: string]: any;
}

/**
 * Gets the translated fields for an agent based on the current language
 * Falls back to first available translation if current language not found
 */
export function getAgentTranslation(
  agent: Agent,
  currentLanguage: string,
  languages: { id: string; code: string; isDefault?: boolean }[]
): AgentTranslation {
  if (!agent.translations || agent.translations.length === 0) {
    // Fallback to base agent fields if no translations available
    return {
      languageId: '',
      name: agent.name || 'Unnamed Agent',
      description: agent.description || '',
      chatTitle: agent.name || 'Chat',
      welcomeMessage: '',
    } as AgentTranslation;
  }

  // Resolve 'auto' to actual language
  const resolvedLanguage = currentLanguage === 'auto' 
    ? resolveAutoLanguage(['en', 'es-MX'])
    : currentLanguage;

  // Try to find translation matching the language code
  // Note: translations come from backend with languageId that might be a code like 'en' or 'es-MX'
  const langId = languages.find(l => l.code === resolvedLanguage)?.id;
  let translation = agent.translations.find(t => t.languageId === langId);  

  // If not found, try to match by base language code (e.g., 'en' from 'en-US')
  if (!translation) {
    translation = agent.translations.find(t => {
      const transLangId = languages.find(l => l.code.split('-')[0] === t.languageId?.split('-')[0])?.id;
      return transLangId === langId;
    });
  }

  // If still not found, use the first available translation
  if (!translation) {
    translation = agent.translations[0];
  }

  return {
    languageId: translation.languageId || '',
    name: translation.name || agent.name || 'Unnamed Agent',
    description: translation.description || agent.description || '',
    chatTitle: translation.chatTitle || translation.name || agent.name || 'Chat',
    welcomeMessage: translation.welcomeMessage || '',
  };
}

